/**
 * useVideoFilterOptions Hook
 * Loads the choices a video filter UI renders: the tag vocabulary, the people who have
 * captured footage, and the teams whose players have any.
 */

import { useCallback, useEffect, useState } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import type { VideoFilterOptions } from '../types/prospect-media';
import { useAuth } from './useAuth';

const EMPTY: VideoFilterOptions = { tags: [], uploaders: [], teams: [] };

export function useVideoFilterOptions() {
  const { effectiveOrganizationId } = useAuth();
  const [options, setOptions] = useState<VideoFilterOptions>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setOptions(await prospectMediaService.getVideoFilterOptions());
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load filter options';
      console.error('[useVideoFilterOptions] Failed to fetch filter options', err);
      setError(message);
      // Fall back to no choices rather than stale ones from the previous organization.
      setOptions(EMPTY);
    } finally {
      setLoading(false);
    }
  }, [effectiveOrganizationId]);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  return { options, loading, error, refresh: fetchOptions };
}
